import React, { useState, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogActions, DialogTitle, Button } from '@mui/material';
import { Field, Form } from 'react-final-form';
import renderTextField from 'components/textBox';
import { required, emailField, specailChar, validatePhone } from 'constants/validations';
import { NotificationManager } from 'react-notifications';
import axios from 'constants/axios';

const UserInfoDialog = ({ open, onClose, onSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingUser, setIsFetchingUser] = useState(false);
  const debounceTimerRef = useRef(null);
  const previousPhoneRef = useRef('');
  const formRef = useRef(null);

  const composeValidators = useCallback(
    (...validators) => (value, allValues) =>
      validators.reduce((error, validator) => error || validator(value, allValues), undefined),
    []
  );

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const fetchUserByPhone = useCallback(async (phone) => {
    if (!phone || phone.length !== 10 || !formRef.current) {
      return;
    }

    if (phone === previousPhoneRef.current) {
      return;
    }

    previousPhoneRef.current = phone;
    setIsFetchingUser(true);

    try {
      const { data } = await axios.get(`/tempUser/getUserByPhone?phone=${phone}`);

      if (data?.user) {
        const { name, email, city, company_name } = data.user;

        formRef.current.batch(() => {
          if (name) formRef.current.change('name', name);
          if (email) formRef.current.change('email', email);
          if (city) formRef.current.change('city', city);
          if (company_name) formRef.current.change('shop_name', company_name);
        });

        NotificationManager.info("User details auto-filled");
      }
    } catch (err) {
      if (err.response?.status !== 404) {
        console.error("Error fetching user data:", err);
        NotificationManager.error("Error fetching user data");
      }
    } finally {
      setIsFetchingUser(false);
    }
  }, []);

  const debouncedFetchUser = useCallback((phone) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (!phone || phone.length !== 10) {
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      fetchUserByPhone(phone);
    }, 800);
  }, [fetchUserByPhone]);

  const onSubmit = useCallback(async (values) => {
    if (!values.phone || values.phone.length !== 10) {
      NotificationManager.error("Please enter a valid 10-digit phone number");
      return;
    }
    values.company_name = values.shop_name;
    setIsLoading(true);
    try {
      const { data } = await axios.post('/tempUser/create', values);

      if (!data?.token) {
        throw new Error('No token received');
      }

      localStorage.setItem('temp_token', data.token);
      localStorage.setItem('start_time', new Date().toISOString());

      NotificationManager.success("You can now start browsing!");
      onSuccess?.();

      setTimeout(() => {
        onClose();
        window.location.reload();
      }, 500);

    } catch (err) {
      const errorMsg = err.response?.data?.message || "Something went wrong, please try again";
      NotificationManager.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [onClose, onSuccess]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose();
    }
  }, [isLoading, onClose]);


  const PhoneField = useCallback((props) => {
    const { input, meta, ...rest } = props;
    
    const handleChange = (e) => {
      const value = e.target.value?.replace(/\D/g, '').slice(0, 10);
      input.onChange(value);
      debouncedFetchUser(value);
    };

    const handleBlur = (e) => {
      input.onBlur(e);
      const value = input.value;
      if (value && value.length === 10) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }
        fetchUserByPhone(value);
      }
    };

    return renderTextField({
      ...rest,
      input: {
        ...input,
        onChange: handleChange,
        onBlur: handleBlur,
      },
      meta,
    });
  }, [debouncedFetchUser, fetchUserByPhone]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>
        <h2 style={{ margin: 0, textAlign: 'center', fontSize: '1.5rem' }}>
          Start Browsing Session
        </h2>
        <p style={{
          margin: '8px 0 0',
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#666'
        }}>
          Enter your details to continue
        </p>
      </DialogTitle>

      <DialogContent>
        <Form
          onSubmit={onSubmit}
          render={({ handleSubmit, form, submitting, pristine }) => {
            // Store form reference
            formRef.current = form;

            return (
              <form onSubmit={handleSubmit} autoComplete="off" noValidate>
                <Field
                  name="phone"
                  component={PhoneField}
                  label="Phone Number *"
                  placeholder="Enter 10-digit phone number"
                  validate={validatePhone}
                  disabled={isLoading}
                  fullWidth
                  margin="normal"
                  helperText={isFetchingUser ? "Checking for existing user..." : ""}
                />

                <Field
                  name="name"
                  component={renderTextField}
                  label="Full Name *"
                  placeholder="Enter your full name"
                  validate={composeValidators(required, specailChar)}
                  disabled={isLoading || isFetchingUser}
                  fullWidth
                  margin="normal"
                />

                <Field
                  name="email"
                  component={renderTextField}
                  label="Email *"
                  placeholder="your.email@example.com"
                  validate={emailField}
                  type="email"
                  disabled={isLoading || isFetchingUser}
                  fullWidth
                  margin="normal"
                />

                <Field
                  name="city"
                  component={renderTextField}
                  label="City *"
                  placeholder="Enter your city"
                  validate={composeValidators(required, specailChar)}
                  disabled={isLoading || isFetchingUser}
                  fullWidth
                  margin="normal"
                />

                <Field
                  name="shop_name"
                  component={renderTextField}
                  label="Shop/Business Name *"
                  placeholder="Enter shop name"
                  validate={composeValidators(required, specailChar)}
                  disabled={isLoading || isFetchingUser}
                  fullWidth
                  margin="normal"
                />

                <DialogActions style={{ padding: '16px 0 0', justifyContent: 'space-between' }}>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    disabled={isLoading || isFetchingUser}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={submitting || pristine || isLoading || isFetchingUser}
                  >
                    {isLoading ? 'Processing...' : isFetchingUser ? 'Loading...' : 'Start Browsing'}
                  </Button>
                </DialogActions>
              </form>
            );
          }}
        />
      </DialogContent>
    </Dialog>
  );
};

export default UserInfoDialog;